import React, { useEffect, useRef } from "react";
import { useFieldArray } from "react-hook-form";
import { Card } from "antd";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";

import BaseButton from "../Button/BaseButton";
import { BaseFormFieldsListProps } from "./types";
import FormFieldWrapper from "@/components_v2/Wrapper/FormFieldWrapper";

import "./styles.css";

const BaseFormFieldsList = ({
  name,
  control,
  title,
  description,
  fields,
  addButtonText,
  defaultValues = {},
  className,
  headerTitle,
}: BaseFormFieldsListProps) => {
  const {
    fields: formFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name,
  });
  const initialAppendRef = useRef(false);

  useEffect(() => {
    if (formFields.length === 0 && !initialAppendRef.current) {
      initialAppendRef.current = true;
      append(defaultValues);
    }
  }, [formFields.length, append]);

  const handleRemoveItem = (index: number) => {
    if (formFields.length === 1) {
      return;
    }
    remove(index);
  };

  return (
    <div className={`flex flex-col gap-200 ${className && className}`}>
      <div className="flex flex-col">
        {title && <h6 className="body-s-medium">{title}</h6>}
        {description && <p className="body-s-regular">{description}</p>}
      </div>

      <div className="base-form-fields-list-content flex flex-col gap-300">
        {formFields.map((formField, index) => (
          <Card
            key={formField.id}
            size="small"
            extra={
              formFields.length > 1 ? (
                <BaseButton
                  variant="link"
                  size="small"
                  leadingIcon={<CloseOutlined />}
                  onClick={() => handleRemoveItem(index)}
                />
              ) : null
            }
            title={`${index + 1}. ${headerTitle}`}
          >
            <div className="base-form-fields-list-item flex flex-col gap-200">
              {fields.map((field) => {
                const Component = field.component;
                const fieldName = `${name}.${index}${field.name ? `.${field.name}` : ""}`;

                let disabled;
                if (field.disabled && typeof field.disabled === "function") {
                  disabled = field.disabled(index);
                }
                return (
                  <div
                    key={`${formField.id}-${field.name}`}
                    className="base-form-field-container"
                  >
                    <FormFieldWrapper
                      label={field.label}
                      required={field.required}
                    >
                      <Component
                        {...field.props}
                        name={fieldName}
                        disabled={disabled}
                      />
                    </FormFieldWrapper>
                  </div>
                );
              })}
            </div>
          </Card>
        ))}

        <BaseButton
          variant="link"
          size="small"
          leadingIcon={<PlusOutlined />}
          onClick={() => append(defaultValues)}
        >
          {addButtonText}
        </BaseButton>
      </div>
    </div>
  );
};

export default BaseFormFieldsList;
